import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { DismissibleTag } from "@carbon/react";
import DataTable from "../../../components/carbon/DataTable/DataTable";
import EmptyState from "../../../components/carbon/EmptyState/EmptyState";
import { useNotification } from "../../../context";
import { queryKeys } from "../../../hooks/queryKeys";
import {
  useDeleteRequestItemV2,
  useUpdateRequestItemV2,
} from "../../../hooks/usePurchaseRequestsV2";
import { PurchaseRequestItemState, PurchaseRequestItemV2 } from "../../../types";
import { computeBudgetSummary } from "./detailHelpers";
import { DetailPermissions } from "./useDetailPermissions";
import { DetailItemsData } from "./useDetailItems";
import DetailItemFilters from "./DetailItemFilters";
import DetailItemInfoPanel from "./DetailItemInfoPanel";
import EditItemModal from "./EditItemModal";
import { detailItemHeaders, mapItemToDetailRow } from "./detailItemRows";

export interface DetailItemsTabProps {
  id: string | undefined;
  items: DetailItemsData;
  permissions: DetailPermissions;
  globalCurrency: string;
  selectedItemIds: string[];
  onSelectedItemIdsChange: (ids: string[]) => void;
  onRejectItem: (item: PurchaseRequestItemV2) => void;
  onResendItem: (item: PurchaseRequestItemV2) => void;
  isBusy: boolean;
  langEs: boolean;
}

/**
 * The "Items" tab (Task 5) — the pre-Carbon raw `<table>` rebuilt on the shared `DataTable`,
 * including its `selection` prop (bulk-select for the assigned buyer) with `isRowDisabled` gating
 * exactly the states that could never be selected before (anything other than IN_APPROVAL). The
 * item STATE MACHINE display, per-item edit (owner) and per-item reject (assigned buyer) actions,
 * the material-info slide-over, search and every filter are preserved.
 */
const DetailItemsTab = ({
  id,
  items,
  permissions,
  globalCurrency,
  selectedItemIds,
  onSelectedItemIdsChange,
  onRejectItem,
  onResendItem,
  isBusy,
  langEs,
}: DetailItemsTabProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { openNotification } = useNotification();

  const [editingItem, setEditingItem] = useState<PurchaseRequestItemV2 | null>(
    null
  );
  const [infoItem, setInfoItem] = useState<PurchaseRequestItemV2 | null>(null);
  const [infoSlideOpen, setInfoSlideOpen] = useState(false);

  useEffect(() => {
    if (!infoSlideOpen) setInfoItem(null);
  }, [infoSlideOpen]);

  const { mutate: updateItem, isPending: isUpdatingItem } =
    useUpdateRequestItemV2(() => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.PURCHASE_REQUEST_V2, id],
      });
      openNotification(t("purchaseRequests.editSuccess"));
      setEditingItem(null);
    });

  const { mutate: deleteItem, isPending: isDeletingItem } =
    useDeleteRequestItemV2(() => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.PURCHASE_REQUEST_V2, id],
      });
      setEditingItem(null);
    });

  const busy = isBusy || isUpdatingItem || isDeletingItem;

  const { activeItems } = items;
  const { canEditRequest, canBuyerReject, isAssignedBuyer } = permissions;

  const { showBudgetPerItem } = computeBudgetSummary(activeItems);
  const showActions = canEditRequest || canBuyerReject;

  const openInfoSlide = (item: PurchaseRequestItemV2) => {
    setInfoItem(item);
    setInfoSlideOpen(true);
  };

  const handleChangeEditingItem = (
    key: keyof PurchaseRequestItemV2,
    value: unknown
  ) => {
    setEditingItem((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const handleSaveItem = () => {
    if (!editingItem || !canEditRequest) return;
    updateItem({
      itemId: editingItem.id,
      body: {
        ...editingItem,
        budgetCurrency: editingItem.currency ?? null,
      },
    });
  };

  const handleDeleteItem = () => {
    if (!editingItem || !canEditRequest) return;
    deleteItem(editingItem.id);
  };

  const headers = detailItemHeaders(t, {
    showBudgetPerItem,
    showActions,
    showGroup: items.groupOptions.length > 0,
  });

  const rows = useMemo(
    () =>
      activeItems.map((item) =>
        mapItemToDetailRow(item, t, {
          onOpenInfo: openInfoSlide,
          onEdit: (i) => canEditRequest && setEditingItem(i),
          onReject: onRejectItem,
          onResend: onResendItem,
          canEditRequest,
          canBuyerReject,
          isBusy: busy,
        })
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeItems, t, canEditRequest, canBuyerReject, busy, onResendItem]
  );

  return (
    <div className="pr-detail__items-tab">
      <DetailItemFilters
        searchQuery={items.searchQuery}
        onSearchChange={items.setSearchQuery}
        tagOptions={items.tagOptions}
        selectedTagIds={items.selectedTagIds}
        onTagIdsChange={items.setSelectedTagIds}
        categoryOptions={items.categoryOptions}
        selectedCategoryIds={items.selectedCategoryIds}
        onCategoryIdsChange={items.setSelectedCategoryIds}
        buyerOptions={items.buyerOptions}
        selectedBuyerIds={items.selectedBuyerIds}
        onBuyerIdsChange={items.setSelectedBuyerIds}
        stateOptions={items.stateOptions}
        selectedStates={items.selectedStates}
        onStatesChange={items.setSelectedStates}
        groupOptions={items.groupOptions}
        selectedGroups={items.selectedGroups}
        onGroupsChange={items.setSelectedGroups}
        hasAnyFilter={items.hasAnyFilter}
        onClearAll={items.clearAllFilters}
      />

      {isAssignedBuyer && selectedItemIds.length > 0 ? (
        <div className="pr-detail__selected">
          <p className="pr-detail__selected-label">
            {t("tenders.selectedMaterials")} ({selectedItemIds.length})
          </p>
          <div className="pr-detail__selected-chips">
            {selectedItemIds.map((itemId) => {
              const item = activeItems.find((i) => i.id === itemId);
              const label =
                item?.material?.globalMaterialName || String(itemId);
              return (
                <DismissibleTag
                  key={itemId}
                  type="blue"
                  size="md"
                  text={label}
                  title={label}
                  onClose={() =>
                    onSelectedItemIdsChange(
                      selectedItemIds.filter((sid) => sid !== itemId)
                    )
                  }
                />
              );
            })}
          </div>
        </div>
      ) : null}

      <DataTable
        headers={headers}
        rows={rows}
        isLoading={items.isItemsLoading}
        selection={
          isAssignedBuyer
            ? {
                selectedIds: selectedItemIds,
                onChange: (ids) => onSelectedItemIdsChange(ids as string[]),
                getRowLabel: (row) => {
                  const original = activeItems.find(
                    (item) => item.id === row.id
                  );
                  const label =
                    original?.material?.globalMaterialName || String(row.id);
                  return `${t("purchaseRequests.columns.name")}: ${label}`;
                },
                selectAllLabel: t("order.selectAll"),
                hideDisabledSelection: true,
                isRowDisabled: (row) => {
                  const original = activeItems.find(
                    (item) => item.id === row.id
                  );
                  return original
                    ? original.state !== PurchaseRequestItemState.IN_APPROVAL
                    : false;
                },
              }
            : undefined
        }
        empty={<EmptyState title={t("purchaseRequests.items.empty")} />}
      />

      <EditItemModal
        item={editingItem}
        globalCurrency={globalCurrency}
        isBusy={busy}
        canEditRequest={canEditRequest}
        onChange={handleChangeEditingItem}
        onSave={handleSaveItem}
        onDelete={handleDeleteItem}
        onClose={() => setEditingItem(null)}
      />

      <DetailItemInfoPanel
        requestId={id}
        item={infoItem}
        open={infoSlideOpen}
        setOpen={setInfoSlideOpen}
        langEs={langEs}
      />
    </div>
  );
};

export default DetailItemsTab;
